<template>
  <div class="shop-the-look">
    <template
      v-if="hasMobileProducts && outfit?.mobileCollageTemplate"
    >
      <div>
        <shop-the-look-collage
          :products="mobileProducts"
          :template="outfit?.mobileCollageTemplate"
          :outfit="outfit"
          :outfit-index="outfitIndex"
          :active-index="slideIndex"
          @product-click="handleProductClick"
          @shopping-started="onShoppingStarted"
        />
      </div>
    </template>

    <template v-else-if="isMobile && state !== 'ready'">
      <div
        class="shop-the-look__heading"
        v-html="'&nbsp;'"
      />
      <div class="shop-the-look__collage-loading" />
    </template>
  </div>
</template>

<script>

/**
 * Vue: Shop the look (shop-the-look)
 * -----------------------------------------------------------------------------
 * Shop the Look section.
 *
 * @param {String} source - Optional source to include when adding to cart
 * @param {Object} outfit - The outfit metaobject data
 * @param {Object} mainProduct - The main product for which outfit is added
 *
 */
import { mapActions, mapState } from 'vuex'

import ShopTheLookCollage from '~async/shop-the-look/shop-the-look-collage'

export default {
  name: 'ShopTheLook',

  components: {
    ShopTheLookCollage,
  },

  props: {
    source: {
      type: [String, null],
      default: null,
    },
    outfit: {
      type: Object,
      required: true,
    },
    mainProduct: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    outfitIndex: {
      type: Number,
      default: 0,
    },
    slideIndex: {
      type: Number,
      default: null,
    },
  },

  emits: ['update-active-slide'],

  data() {
    return {
      fadeIn: false,
      state: 'loading',
      showBackInStock: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute outfit products or loading state.
     * @returns {Array<Object>}
     */
    outfitProducts() {
      const products = [
        this.outfit?.product1 || null,
        this.outfit?.product2 || null,
        this.outfit?.product3 || null,
        this.outfit?.product4 || null,
        this.outfit?.product5 || null,
      ]

      return this.isMobile
        ? products
        : products.filter((prod) => prod && prod.id !== this.mainProduct.id)
    },

    /**
     * If there are mobile outfit products
     * @returns {Boolean}
     */
    hasMobileProducts() {
      return Boolean(this.outfit?.product1) ||
        Boolean(this.outfit?.product2) ||
        Boolean(this.outfit?.product3) ||
        Boolean(this.outfit?.product4) ||
        Boolean(this.outfit?.product5)
    },

    /**
     * The mobile products
     * @returns {Array}
     */
    mobileProducts() {
      return [
        this.outfit?.product1 || null,
        this.outfit?.product2 || null,
        this.outfit?.product3 || null,
        this.outfit?.product4 || null,
        this.outfit?.product5 || null,
      ]
    },
  },

  watch: {

    /**
     * Watch state to update fade in.
     */
    async state() {
      if (this.state === 'loading') {
        this.fadeIn = false
        return
      }

      await this.$nextTick()
      this.fadeIn = true
    },
  },

  methods: {

    /**
     * Overlay actions
     */
    ...mapActions({
      openOverlay: 'overlays/open',
      setProductFormDrawerProduct: 'productFormDrawer/setProductFormDrawerProduct',
      setConfig: 'productFormDrawer/setConfig',
    }),

    /**
     * Get product image.
     * @param {Object} product - STL product.
     * @returns {Boolean|String}
     */
    getProductImage(product) {
      if (this.state === 'loading') {
        return {}
      }

      return product.selectedOrFirstAvailableVariant?.image ||
        product.variants[0]?.image ||
        product.media[0]?.image
    },

    /**
     * Open the quick add on product click
     * @param {Object} Product
     */
    handleProductClick(product) {
      if (product.loading) {
        return
      }

      const config = {
        strings: {
          return: this.$string('shop_the_look.return_to_look'),
          back: this.$string('shop_the_look.back_to_look'),
        },
        bisNamespace: 'shop-the-look',
      }

      if (this.source) {
        config.location = {
          key: 'pdpShopTheLook',
          metaobjectName: this.outfit?.metaobjectName || null,
        }
      }

      this.setProductFormDrawerProduct(product)
      this.setConfig(config)

      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'product-form-drawer',
      })
    },

    /**
     * Handles back in stock modal display
     * @param {Boolean} isActive - back in stock display state
     */
    handleBackInStockModal(isActive) {
      this.showBackInStock = isActive
    },

    /**
     * Sets the heading text
     * @param {String} text - heading text
     */
    setHeadingText(text) {
      this.$refs.shopTheLookHeading.innerHTML = text
    },
    onShoppingStarted(index) {
      this.$emit('update-active-slide', index)
    },
  },
}
</script>

<style lang="scss">
@import './shop-the-look';
</style>
